import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';
import SEO from '../../common/SEO';
import GoogleMaps from '../../components/GoogleMaps';
import ServicesNavigationFooter from '../../components/ServicesNavigationFooter';
import RelatedTopics from '../../components/RelatedTopics';
import * as FiIcons from 'react-icons/fi';

const { 
  FiArrowLeft, FiShield, FiCheckCircle, FiPieChart, FiTrendingUp, 
  FiList, FiAlertTriangle, FiDollarSign, FiActivity, FiLayers, 
  FiFileText, FiCheckSquare, FiSearch, FiBriefcase, FiInfo, FiCheck 
} = FiIcons;

function ConstructionCosts() {
  const articleSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        "headline": "Baukosten richtig abschätzen: Praxisleitfaden nach DIN 276 (Update 2026)",
        "alternativeHeadline": "Wie Sie Baukosten kalkulieren und Budgetüberschreitungen vermeiden",
        "image": "https://mx.ausbau.nrw/img/Trocken_und_Akustikbau_xl.jpg",
        "author": {
          "@type": "Person",
          "name": "Maxim Kelm",
          "jobTitle": "Geschäftsführer MK-AUSBAU",
          "url": "https://ausbau.nrw/ueber"
        },
        "publisher": {
          "@type": "Organization",
          "name": "MK-AUSBAU",
          "logo": {
            "@type": "ImageObject",
            "url": "https://ausbau.nrw/logo-icon.svg"
          }
        },
        "datePublished": "2026-03-01",
        "dateModified": "2026-03-18",
        "description": "Baukosten kalkulieren wie ein Profi: Alles zur DIN 276, Baunebenkosten & Risikozuschlägen. Der ultimative Guide für Neubau und Sanierung.",
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": "https://ausbau.nrw/#/blog/baukosten-abschaetzen"
        }
      },
      {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "Wie hoch sind die Baunebenkosten 2026?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Die Baunebenkosten (Architekt, Statik, Gebühren) liegen 2026 im Schnitt zwischen 15 % und 20 % der reinen Baukosten (Kostengruppen 300 und 400)."
            }
          },
          {
            "@type": "Question",
            "name": "Warum ist eine Kostenschätzung nach DIN 276 wichtig?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Die DIN 276 bietet eine einheitliche Struktur. Sie stellt sicher, dass keine Kostenpositionen (wie Außenanlagen oder Technik) vergessen werden und ermöglicht die Vergleichbarkeit von Angeboten."
            }
          }
        ]
      }
    ]
  };

  return (
    <div className="py-20 bg-white">
      <SEO 
        title="Baukosten abschätzen 2026: DIN 276 Guide & Checkliste" 
        description="Baukosten kalkulieren ohne böse Überraschungen! 🏗️ Alles zu DIN 276, Baunebenkosten & Risikopuffern. Experten-Tipps für Ihr Budget." 
        keywords="Baukosten abschätzen, Kostenschätzung DIN 276, Baukosten pro qm 2026, Baunebenkosten Liste, Sanierungskosten kalkulieren, HOAI Kostenberechnung"
        schema={articleSchema}
        canonical="/blog/baukosten-abschaetzen"
      />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link 
          to="/blog" 
          className="inline-flex items-center text-mk-teal-600 hover:text-mk-teal-700 font-bold mb-8 transition-colors group"
        >
          <SafeIcon icon={FiArrowLeft} className="mr-2 w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          Zurück zur Übersicht
        </Link>

        {/* ... Rest der Komponente bleibt gleich ... */}
        <motion.article 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="prose prose-lg prose-mk max-w-none"
        >
          {/* ... Inhalt ... */}
          <header className="mb-12">
            <div className="flex items-center space-x-3 text-mk-teal-600 font-bold uppercase tracking-wider text-sm mb-4">
              <SafeIcon icon={FiPieChart} />
              <span>Kostenplanung & Management</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight mb-6">
              Baukosten richtig abschätzen: Der ultimative Praxisleitfaden für sichere Budgets (2026) 📈
            </h1>
            <div className="flex items-center space-x-4 text-gray-500 text-sm border-y border-gray-100 py-4">
              <span className="flex items-center"><SafeIcon icon={FiShield} className="mr-1" /> Experten-Ratgeber</span>
              <span className="flex items-center"><SafeIcon icon={FiCheckCircle} className="mr-1" /> Aktualisiert: März 2026</span>
            </div>
          </header>

          <div className="bg-mk-teal-50 border-l-4 border-mk-teal-500 p-6 mb-12 rounded-r-xl">
            <h2 className="text-mk-teal-900 font-bold mt-0 mb-2 text-lg">Zusammenfassung: Wie man Baukosten kalkuliert</h2>
            <p className="text-mk-teal-800 m-0 text-base leading-relaxed">
              Eine präzise Baukostenermittlung erfolgt in drei Stufen: 1. <strong>Kostenschätzung</strong> (Basis: m² oder m³), 2. <strong>Kostenberechnung</strong> (Basis: Entwurfsplanung nach DIN 276) und 3. <strong>Kostenanschlag</strong> (Basis: echte Angebote). <br/><br/>
              <strong>Wichtig:</strong> Planen Sie 2026 bei Neubauten mit ca. 3.200 - 3.900 €/m² Wohnfläche und kalkulieren Sie bei Sanierungen immer einen <strong>Risikopuffer von mindestens 20 %</strong> ein.
            </p>
          </div>

          <div className="lead text-xl text-gray-600 leading-relaxed mb-12">
            Ein Bauprojekt ohne präzise Kostenplanung ist wie eine Fahrt im Nebel ohne Navi. Wer die <strong>Baukosten falsch abschätzt</strong>, riskiert nicht nur die Finanzierung, sondern sein gesamtes Vermögen. In diesem Guide zeigen wir Ihnen, wie Profis kalkulieren.
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6">
            1. Struktur schaffen mit der DIN 276
          </h2>
          <p className="text-gray-700 mb-6">
            Die DIN 276 ist die "Sprache" der Kostenplaner. Sie unterteilt ein Projekt in Kostengruppen (KG). Nur wer diese Struktur nutzt, vergisst keine wichtigen Positionen wie die Erschließung oder die Technik.
          </p>

          <div className="overflow-x-auto mb-12 rounded-lg border border-gray-200 shadow-sm">
            <table className="min-w-full text-sm text-left text-gray-700">
              <thead className="text-xs text-white uppercase bg-mk-teal-600">
                <tr>
                  <th className="px-6 py-4 w-20">KG</th>
                  <th className="px-6 py-4">Bezeichnung</th>
                  <th className="px-6 py-4">Inhalt / Beispiele</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                <tr>
                  <td className="px-6 py-4 font-bold">100</td>
                  <td className="px-6 py-4 font-medium">Grundstück</td>
                  <td className="px-6 py-4 text-gray-500">Kaufpreis, Notar, Grunderwerbsteuer</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 font-bold">300</td>
                  <td className="px-6 py-4 font-medium">Bauwerk – Baukonstruktion</td>
                  <td className="px-6 py-4 text-gray-500">Rohbau, Dach, Fenster, Innenausbau, Trockenbau</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-bold">400</td>
                  <td className="px-6 py-4 font-medium">Technische Anlagen</td>
                  <td className="px-6 py-4 text-gray-500">Heizung, Sanitär, Elektro, Lüftung</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 font-bold">700</td>
                  <td className="px-6 py-4 font-medium">Baunebenkosten</td>
                  <td className="px-6 py-4 text-gray-500">Architekt, Statiker, Baugenehmigung</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6 flex items-center">
            <SafeIcon icon={FiAlertTriangle} className="mr-3 text-mk-red-600" /> 2. Die größten Budgetfallen bei der Sanierung
          </h2>
          <p className="text-gray-700 mb-6">
            Besonders bei Bestandsimmobilien weichen die realen Kosten oft massiv von der ersten Schätzung ab. Das liegt meist an "unsichtbaren" Faktoren.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
              <h4 className="font-bold text-gray-900 mt-0 mb-3 text-lg">Schadstoffe (Asbest/PCB)</h4>
              <p className="text-sm text-gray-600 m-0">
                Bei Gebäuden vor 1990 ist eine <Link to="/blog/ankaufspruefung-mehrfamilienhaus" className="text-mk-teal-600 underline">Schadstoffprüfung</Link> Pflicht. Eine fachgerechte Entsorgung kann die Kosten für den Rückbau verfünffachen.
              </p>
            </div>
            <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
              <h4 className="font-bold text-gray-900 mt-0 mb-3 text-lg">Brandschutz-Upgrades</h4>
              <p className="text-sm text-gray-600 m-0">
                Alte Decken erfüllen oft nicht die Anforderungen an F30 oder F90. Die Ertüchtigung (z.B. durch speziellen <Link to="/trockenbau" className="text-mk-teal-600 underline">Brandschutz-Trockenbau</Link>) muss eingepreist werden.
              </p>
            </div>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6">
            3. Praxis-Benchmarks pro Quadratmeter (2026)
          </h2>
          <p className="text-gray-700 mb-6">
            Diese Werte dienen als erste Orientierung für Projekte im Raum NRW/Heinsberg (inkl. MwSt. & Montage):
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-12">
            <div className="bg-gray-50 p-4 rounded-xl text-center">
              <strong className="block text-gray-900 mb-2">Malerarbeiten</strong>
              <span className="text-mk-teal-600 font-bold">25 – 45 € / m²</span>
              <span className="block text-[10px] text-gray-500 mt-1">Wandfläche (Q3 Standard)</span>
            </div>
            <div className="bg-gray-50 p-4 rounded-xl text-center border border-mk-teal-100 shadow-sm">
              <strong className="block text-gray-900 mb-2">Trockenbauwand</strong>
              <span className="text-mk-teal-600 font-bold">65 – 95 € / m²</span>
              <span className="block text-[10px] text-gray-500 mt-1">Ständerwand inkl. Dämmung</span>
            </div>
            <div className="bg-gray-50 p-4 rounded-xl text-center">
              <strong className="block text-gray-900 mb-2">Designboden (PU)</strong>
              <span className="text-mk-teal-600 font-bold">90 – 140 € / m²</span>
              <span className="block text-[10px] text-gray-500 mt-1">Fugenlos, inkl. Vorbereitung</span>
            </div>
          </div>

          <div className="bg-mk-red-50 border-l-4 border-mk-red-600 p-6 rounded-r-xl my-12">
            <h3 className="text-mk-red-900 font-bold mt-0 mb-2 flex items-center">
              <SafeIcon icon={FiAlertTriangle} className="mr-2" /> Der "10-Prozent-Irrtum"
            </h3>
            <p className="text-mk-red-800 m-0 text-sm leading-relaxed">
              Viele Bankberater empfehlen einen Puffer von 10 %. Unsere Erfahrung aus hunderten Projekten zeigt: Das reicht bei Sanierungen fast nie! Rechnen Sie mit <strong>20 % Puffer</strong> für Unvorhergesehenes. Wenn Sie ihn nicht brauchen, haben Sie am Ende eine Sondertilgung gewonnen. Wenn Sie ihn brauchen, rettet er Ihr Projekt.
            </p>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6">
            Fazit: Planung spart bares Geld
          </h2>
          <div className="bg-gray-900 text-white p-10 rounded-[2.5rem] shadow-2xl mb-16 text-center">
            <p className="text-gray-300 mb-8 max-w-2xl mx-auto leading-relaxed">
              Baukosten abschätzen ist keine Hexerei, sondern Fleißarbeit. Wer die DIN 276 als Checkliste nutzt und realistische Marktpreise ansetzt, baut mit einem ruhigen Gewissen.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <span className="bg-white/10 px-4 py-2 rounded-full text-sm font-medium text-mk-teal-300">DIN 276 konform</span>
              <span className="bg-white/10 px-4 py-2 rounded-full text-sm font-medium text-mk-teal-300">20% Risikopuffer</span>
              <span className="bg-white/10 px-4 py-2 rounded-full text-sm font-medium text-mk-teal-300">Aktuelle Benchmarks</span>
            </div>
          </div>

          <div className="text-center py-12 border-t border-gray-100">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Benötigen Sie eine fundierte Kostenschätzung?</h3>
            <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
              Für Ihr Projekt im Kreis Heinsberg erstellen wir Ihnen gerne ein detailliertes Festpreisangebot für unsere Gewerke. So haben Sie von Anfang an Planungssicherheit.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/kontakt" 
                className="bg-mk-teal-500 hover:bg-mk-teal-600 text-white px-8 py-4 rounded-sm font-bold transition-all shadow-lg flex items-center justify-center"
              >
                Kostenanfrage stellen
              </Link>
              <Link 
                to="/blog/wirtschaftliches-bauen" 
                className="bg-mk-red-700 hover:bg-mk-red-800 text-white px-8 py-4 rounded-sm font-bold transition-all shadow-lg flex items-center justify-center"
              >
                Mehr zum wirtschaftlichen Bauen
              </Link>
            </div>
          </div>

        </motion.article>
      </div>

      <RelatedTopics />
      <ServicesNavigationFooter />
      <GoogleMaps />
    </div>
  );
}

export default ConstructionCosts;